<template>
  <div>
    <a-table
      class="custom-table"
      :columns="columns"
      :data-source="dataSource"
      :pagination="{
        total: total,
        showTotal: total => `共 ${total} 条`,
        showSizeChanger: true
      }"
      row-key="id"
      :loading="loading"
      :scroll="{ x: scrollX }"
      :components="components"
      @change="handleTableChange"
    >
    <template slot="name" slot-scope="text, record">
      <a-tooltip placement="topLeft" :title="text">
        <a @click="$router.push({name: 'ServiceDashboardDetail', params: {id: record.id}})">
          {{ text }}
        </a>
      </a-tooltip>
    </template>
    <template slot="status" slot-scope="text">
      <a-tag v-if="text" :color="getStatusColor(text)">{{ $t(`service_status.${text}`) }}</a-tag>
      <span v-else>-</span>
    </template>
    <template slot="level" slot-scope="text">
      <a-tag v-if="text" :color="getLevelColor(text)">{{ $t(`service_level.${text}`) }}</a-tag>
      <span v-else>-</span>
    </template>
    <template slot="order" slot-scope="text">
      <span>{{ text === 0 ? 0 : text || '-'}}</span>
    </template>
    <template slot="busyness" slot-scope="text, record">
      <a-space v-if="getBusyData(record.id)">
        <a-tooltip :title="`繁忙：${getBusyData(record.id).busy || 0}台`">
          <img src="@/assets/images/service-wall/busy.png" alt="" width="13" height="13">
          <span style="margin-left: 2px">{{ getBusyData(record.id).busy || 0 }}</span>
        </a-tooltip>

        <a-tooltip :title="`正常：${getBusyData(record.id).normal || 0}台`">
          <img src="@/assets/images/service-wall/normal.png" alt="" width="13" height="13">
          <span style="margin-left: 2px">{{ getBusyData(record.id).normal || 0 }}</span>
        </a-tooltip>

        <a-tooltip :title="`空闲：${getBusyData(record.id).idle || 0}台`">
          <img src="@/assets/images/service-wall/idle.png" alt="" width="13" height="13">
          <span style="margin-left: 2px">{{ getBusyData(record.id).idle || 0 }}</span>
        </a-tooltip>
      </a-space>
      <span v-else>{{ text === -1 ? '不计入' : text}}</span>
    </template>
    <template slot="health" slot-scope="text">
      <span>{{ text === -1 ? '不计入' : `${text} 分` }}</span>
    </template>
    <template slot="description" slot-scope="record">
      <a-tooltip v-if="record" placement="topLeft" :title="record">{{ record }}</a-tooltip>
      <span v-else>-</span>
    </template>
    <template slot="constructionManager" slot-scope="record">
      <a-tooltip v-if="record" placement="topLeft" :title="record">{{ record }}</a-tooltip>
      <span v-else>-</span>
    </template>
    <template slot="constructionManagerPhone" slot-scope="record">
      <a-tooltip v-if="record" placement="topLeft" :title="record">{{ record }}</a-tooltip>
      <span v-else>-</span>
    </template>
    <template slot="projectManager" slot-scope="record">
      <a-tooltip v-if="record" placement="topLeft" :title="record">{{ record }}</a-tooltip>
      <span v-else>-</span>
    </template>

    <template slot="domains" slot-scope="record">
      <span v-if="!record.length">-</span>
      <template v-else>
        <div class="text-overflow" v-for="item in record" :key="item.id">
          <a-tooltip placement="topLeft" :title="item.name">{{item.name}}</a-tooltip>
        </div>
      </template>
    </template>
    <template slot="sslCertificates" slot-scope="record">
      <span v-if="!record.length">-</span>
      <template v-else>
        <div class="text-overflow" v-for="item in record" :key="item.id">
          <a-tooltip placement="topLeft" :title="item.name">{{item.name}}</a-tooltip>
        </div>
      </template>
    </template>
    <template slot="operation" slot-scope="text, record">
      <edit-button v-permission="{action: 'service.service.update', effect: 'disabled'}" @click="$refs.inputDrawer.show(record.id)"></edit-button>
      <delete-button v-permission="{action: 'service.service.delete', effect: 'disabled'}" @confirm="handleDelete(record.id)"></delete-button>
    </template>
    </a-table>
    <input-drawer ref="inputDrawer" @ok="$emit('ok')"></input-drawer>
  </div>
</template>

<script>
import { deleteService } from '@/api/service'
import DeleteButton from '@/components/button/DeleteButton'
import EditButton from '@/components/button/EditButton'
import InputDrawer from './InputDrawer.vue'

export default {
  name: 'ServiceTable',
  props: {
    dataSource: {
      type: Array,
      default: () => []
    },
    total: {
      type: Number
    },
    loading: {
      type: Boolean
    },
    detailSource: {
      type: Object,
      default: () => {}
    }
  },
  components: { EditButton, DeleteButton, InputDrawer },
  data () {
    return {
      columns: [
        {
          dataIndex: 'name',
          title: '名称',
          width: 180,
          ellipsis: true,
          scopedSlots: {
            customRender: 'name'
          }
        },
        {
          dataIndex: 'level',
          title: '级别',
          width: 90,
          scopedSlots: {
            customRender: 'level'
          }
        },
        {
          dataIndex: 'status',
          title: '状态',
          width: 90,
          scopedSlots: {
            customRender: 'status'
          }
        },
        {
          dataIndex: 'order',
          title: '排序',
          width: 80,
          scopedSlots: {
            customRender: 'order'
          },
          sorter: (a, b) => b.order - a.order,
          defaultSortOrder: 'descend',
          sortDirections: ['descend', 'ascend']
        },
        {
          dataIndex: 'busyness',
          title: '繁忙度',
          width: 160,
          scopedSlots: {
            customRender: 'busyness'
          }
        },
        {
          dataIndex: 'health',
          title: '健康度',
          width: 120,
          scopedSlots: {
            customRender: 'health'
          },
          sorter: (a, b) => a.health - b.health
        },
        {
          dataIndex: 'description',
          title: '描述',
          width: 160,
          ellipsis: true,
          scopedSlots: {
            customRender: 'description'
          }
        },
        {
          dataIndex: 'project_manager',
          title: '系统管理员',
          width: 140,
          ellipsis: true,
          scopedSlots: {
            customRender: 'projectManager'
          }
        },
        {
          dataIndex: 'construction_manager',
          title: '运维人员',
          width: 120,
          ellipsis: true,
          scopedSlots: {
            customRender: 'constructionManager'
          }
        },
        {
          dataIndex: 'construction_manager_phone',
          title: '运维人员电话',
          width: 140,
          ellipsis: true,
          scopedSlots: {
            customRender: 'constructionManagerPhone'
          }
        },
        {
          dataIndex: 'domains',
          title: '域名',
          ellipsis: true,
          width: 140,
          scopedSlots: {
            customRender: 'domains'
          }
        },
        {
          dataIndex: 'ssl_certificates',
          title: 'SSL 证书',
          ellipsis: true,
          width: 140,
          scopedSlots: {
            customRender: 'sslCertificates'
          }
        },
        {
          dataIndex: 'operation',
          title: '操作',
          align: 'center',
          fixed: 'right',
          width: 190,
          scopedSlots: {
            customRender: 'operation'
          }
        }
      ],
      components: {}
    }
  },
  computed: {
    scrollX () {
      let sum = 0
      this.columns.forEach(column => {
        sum += column.width
      })
      return sum
    }
  },
  methods: {
    handleDelete (id) {
      deleteService(id).then(res => {
        this.$message.success(res.message)
        this.$emit('ok')
      })
    },
    handleTableChange (pagination) {
      this.$emit('pageChange', {
        page: pagination.current,
        pageSize: pagination.pageSize
      })
    },
    getStatusColor (status) {
      if (status === 'built') return 'blue'
      if (status === 'building') return 'cyan'
      return ''
    },
    getLevelColor (level) {
      if (level === 2) return '#ffbc75'
      if (level === 3) return '#ffab53'
      if (level === 4) return '#ff9a30'
      if (level === 5) return '#ff890e'
      return '#ffcc98'
    },
    getBusyData (id) {
      const detail = this.detailSource[id] || {}
      const busynessDetail = detail.busynessDetail || []
      return {
        busy: busynessDetail.find(item => item?.key === 'busy')?.value || 0,
        idle: busynessDetail.find(item => item?.key === 'idle')?.value || 0,
        normal: busynessDetail.find(item => item?.key === 'normal')?.value || 0
      }
    }
  }
}
</script>
